
/**
 * Wealthica v2 integration logger
 * 
 * Provides standardized logging with redaction of sensitive data.
 */

import { logger as appLogger } from '@/services/LoggingService';

/**
 * Wealthica-specific logger with standardized context
 */
export const logger = {
  info: (component: string, message: string, data?: Record<string, unknown>) => {
    appLogger.info(`wealthica2.${component}`, message, {
      ...sanitizeForLogging(data || {}),
      provider: 'wealthica2',
      timestamp: new Date().toISOString(),
    });
  },
  
  error: (component: string, message: string, data?: Record<string, unknown>) => {
    appLogger.error(`wealthica2.${component}`, message, {
      ...sanitizeForLogging(data || {}),
      provider: 'wealthica2',
      timestamp: new Date().toISOString(),
    });
  },
  
  warn: (component: string, message: string, data?: Record<string, unknown>) => {
    appLogger.warn(`wealthica2.${component}`, message, {
      ...sanitizeForLogging(data || {}),
      provider: 'wealthica2',
      timestamp: new Date().toISOString(),
    });
  },
  
  debug: (component: string, message: string, data?: Record<string, unknown>) => {
    appLogger.debug(`wealthica2.${component}`, message, {
      ...sanitizeForLogging(data || {}),
      provider: 'wealthica2',
      timestamp: new Date().toISOString(),
    });
  },
};

/**
 * List of sensitive field names that should be redacted from logs
 */
const SENSITIVE_FIELDS = [
  'token',
  'access_token',
  'refresh_token',
  'password',
  'secret',
  'api_key',
  'apiKey',
  'key',
  'credentials'
];

/**
 * Sanitize data for logging by redacting sensitive information
 * 
 * @param data The data to sanitize
 * @returns The sanitized data
 */
export function sanitizeForLogging<T extends Record<string, unknown>>(data: T): Record<string, unknown> {
  // Create a shallow copy of the data
  const result: Record<string, unknown> = { ...data };
  
  // Look for sensitive fields to redact
  Object.keys(result).forEach(key => {
    if (SENSITIVE_FIELDS.includes(key.toLowerCase())) {
      if (typeof result[key] === 'string') {
        const value = result[key] as string;
        // Show first 4 characters and redact the rest
        result[key] = value.length > 4 
          ? `${value.substring(0, 4)}...` 
          : '[REDACTED]';
      } else {
        result[key] = '[REDACTED]';
      }
    } else if (result[key] && typeof result[key] === 'object') {
      // Recursively sanitize nested objects
      result[key] = sanitizeForLogging(result[key] as Record<string, unknown>);
    }
  });
  
  return result;
}
